import {observeResultPresentation, reportResultPresentationError} from '@github-ui/ahp-relay/relay-result-presentation'
import type {ResultPresentationUnknown} from './result-presentation-contract'

const ELEMENT_TIMING_MARKER = 'copilot-result-content'

interface ElementTimingEntry extends PerformanceEntry {
  readonly element: Element | null
  readonly renderTime: number
  readonly identifier: string
  readonly intersectionRect: DOMRectReadOnly
}

function isElementTiming(entry: PerformanceEntry, identifier: string): entry is ElementTimingEntry {
  return (
    entry.entryType === 'element' &&
    'element' in entry &&
    entry.element instanceof Element &&
    'renderTime' in entry &&
    typeof entry.renderTime === 'number' &&
    'identifier' in entry &&
    entry.identifier === identifier &&
    'intersectionRect' in entry &&
    entry.intersectionRect instanceof DOMRectReadOnly
  )
}

interface WatchedElement {
  intersecting?: boolean
  references: number
  contentReferences: number
  mutation?: MutationObserver
  textNodes: Map<Text, Element>
}

interface TimedElement {
  previous: string | null
  owners: Map<HTMLElement, number>
}

export function createResultPresentationObserver(
  onFrame: () => void,
  onPaint: (root: HTMLElement, renderTime: number) => void,
) {
  const timingIdentifier = `${ELEMENT_TIMING_MARKER}:${crypto.randomUUID()}`
  const watched = new Map<HTMLElement, WatchedElement>()
  const timedElements = new Map<Element, TimedElement>()
  let frame: number | undefined
  let disposed = false
  let intersection: IntersectionObserver | undefined
  let performanceObserver: PerformanceObserver | undefined
  let paintSupported = false
  const observePaintEntries = (entries: PerformanceEntry[]) => {
    observeResultPresentation(() => {
      for (const entry of entries) {
        if (!isElementTiming(entry, timingIdentifier)) continue
        const timed = entry.element ? timedElements.get(entry.element) : undefined
        if (!timed || entry.intersectionRect.width <= 0 || entry.intersectionRect.height <= 0) continue
        for (const owner of timed.owners.keys()) onPaint(owner, entry.renderTime)
      }
    })
  }
  const schedule = () => {
    if (disposed || frame !== undefined) return
    observeResultPresentation(() => {
      frame = requestAnimationFrame(() => {
        frame = undefined
        observeResultPresentation(onFrame)
      })
    })
  }
  observeResultPresentation(() => {
    if (typeof IntersectionObserver === 'function') {
      intersection = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (!(entry.target instanceof HTMLElement)) continue
          const target = watched.get(entry.target)
          if (target) {
            target.intersecting =
              entry.isIntersecting && entry.intersectionRect.width > 0 && entry.intersectionRect.height > 0
          }
        }
        schedule()
      })
    }
    if (typeof PerformanceObserver === 'function' && PerformanceObserver.supportedEntryTypes?.includes('element')) {
      performanceObserver = new PerformanceObserver(list => observePaintEntries(list.getEntries()))
      performanceObserver.observe({type: 'element', buffered: true})
      paintSupported = true
    }
  })

  const removeText = (root: HTMLElement, target: WatchedElement, text: Text) => {
    const element = target.textNodes.get(text)
    if (!element) return
    target.textNodes.delete(text)
    const timed = timedElements.get(element)
    const references = timed?.owners.get(root)
    if (!timed || references === undefined) return
    if (references > 1) {
      timed.owners.set(root, references - 1)
      return
    }
    timed.owners.delete(root)
    if (timed.owners.size > 0) return
    timedElements.delete(element)
    if (element.getAttribute('elementtiming') !== timingIdentifier) return
    if (timed.previous === null) element.removeAttribute('elementtiming')
    else element.setAttribute('elementtiming', timed.previous)
  }
  const reconcileText = (root: HTMLElement, target: WatchedElement, text: Text) => {
    const previous = target.textNodes.get(text)
    const element = text.parentElement
    const current =
      root.contains(text) && text.textContent?.trim() && element && !element.closest('button, [aria-hidden="true"]')
        ? element
        : undefined
    if (previous === current) return
    if (previous) removeText(root, target, text)
    if (!current) return
    target.textNodes.set(text, current)
    let timed = timedElements.get(current)
    if (!timed) {
      timed = {previous: current.getAttribute('elementtiming'), owners: new Map()}
      timedElements.set(current, timed)
      current.setAttribute('elementtiming', timingIdentifier)
    }
    timed.owners.set(root, (timed.owners.get(root) ?? 0) + 1)
  }
  const visitText = (node: Node, callback: (text: Text) => void) => {
    if (node instanceof Text) {
      callback(node)
      return
    }
    const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT)
    for (let text = walker.nextNode(); text; text = walker.nextNode()) callback(text as Text)
  }
  const startContentObservation = (root: HTMLElement, target: WatchedElement) => {
    visitText(root, text => reconcileText(root, target, text))
    target.mutation = new MutationObserver(records => {
      observeResultPresentation(() => {
        const affected = new Set<Text>()
        for (const record of records) {
          if (record.type === 'characterData') {
            if (record.target instanceof Text) affected.add(record.target)
            continue
          }
          for (const node of record.removedNodes) visitText(node, text => affected.add(text))
          for (const node of record.addedNodes) visitText(node, text => affected.add(text))
        }
        for (const text of affected) reconcileText(root, target, text)
      })
      schedule()
    })
    target.mutation.observe(root, {childList: true, characterData: true, subtree: true})
  }
  const stopContentObservation = (root: HTMLElement, target: WatchedElement) => {
    target.mutation?.disconnect()
    target.mutation = undefined
    for (const text of [...target.textNodes.keys()]) removeText(root, target, text)
  }
  const remove = (element: HTMLElement, content: boolean) => {
    const target = watched.get(element)
    if (!target) return
    if (content && --target.contentReferences === 0) stopContentObservation(element, target)
    if (--target.references > 0) return
    intersection?.unobserve(element)
    watched.delete(element)
  }

  return {
    schedule,
    flushPaint() {
      if (!disposed && performanceObserver) observePaintEntries(performanceObserver.takeRecords())
    },
    get paintSupported() {
      return paintSupported
    },
    watch(element: HTMLElement, content = false): () => void {
      const current = watched.get(element)
      if (current) {
        current.references++
        if (content && current.contentReferences++ === 0 && paintSupported) {
          observeResultPresentation(() => startContentObservation(element, current))
        }
      } else {
        const target: WatchedElement = {
          references: 1,
          contentReferences: content ? 1 : 0,
          textNodes: new Map(),
        }
        watched.set(element, target)
        observeResultPresentation(() => {
          intersection?.observe(element)
          if (content && paintSupported) startContentObservation(element, target)
        })
      }
      schedule()
      let removed = false
      return () => {
        if (removed) return
        removed = true
        observeResultPresentation(() => remove(element, content))
      }
    },
    visibility(element: HTMLElement, nativeIntersection = false): true | ResultPresentationUnknown | undefined {
      if (document.visibilityState !== 'visible') return 'hidden'
      if (!element.isConnected || element.closest('[hidden], [inert]')) return 'offscreen'
      try {
        if (typeof element.checkVisibility !== 'function') return 'visibility_unsupported'
        if (!element.checkVisibility({checkOpacity: true, checkVisibilityCSS: true})) return 'offscreen'
        if (nativeIntersection) return true
        if (!intersection) return 'visibility_unsupported'
        const intersecting = watched.get(element)?.intersecting
        return intersecting === undefined ? undefined : intersecting ? true : 'offscreen'
      } catch {
        reportResultPresentationError('observer')
        return 'observer_failed'
      }
    },
    dispose() {
      disposed = true
      if (frame !== undefined) cancelAnimationFrame(frame)
      performanceObserver?.disconnect()
      intersection?.disconnect()
      for (const [element, target] of watched) {
        target.references = 1
        target.contentReferences = target.contentReferences > 0 ? 1 : 0
        remove(element, target.contentReferences > 0)
      }
    },
  }
}
